import { MapSitesService } from 'app/map/services/map_sites.service';
import { Selection } from 'app/shared/selection/Selection';
import {
    HIDE_CONTEXT_MENU_EVENT,
    SHOW_CONTEXT_MENU_EVENT,
} from 'app/context_menu/context_menu.component';
import { CTRL, KeypressService } from 'app/shared/keypress.service';
import { SelectionService } from 'app/shared/selection/SelectionService';
import { MapService } from 'app/map/services/map.service';
import { GeoDataService } from './heatmap/GeoDataService';
import { formatSelectableFeatureId } from 'app/map/services/heatmap/AbstractGeoDataMap';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { CesiumZoneService } from 'app/map/geometry/cesium/CesiumZones/CesiumZoneService';
import { CesiumAssetManager } from '../geometry/cesium/CesiumAssetManager';
import { SiteStateService } from 'app/shared/site_state.service';
import {
    AbstractMapInputHandler,
    IMapInputHandler,
} from 'app/map/services/map_input_handling/MapInputHandler';
import { k_SHOW_MAP_BOUNDARIES_BANNER, PanesService } from 'app/main/PanesService';

// .id is not any of our ID's. It's Cesium's drillPick object.
export const pickedToEntitiesReducer = (
    cesiumAssetManager,
    panesService,
    selections: Selection[],
    pick,
) => {
    let selection: Selection = undefined;

    if (pick) {
        // Entity
        if (pick.id && pick.id.getId) {
            selection = new Selection(
                pick.id.getId(),
                pick.id.selectionType,
                pick.id.selectionSubType,
            );
        }
        // Primitive
        else if (pick.primitive && pick.primitive.getId) {
            if (pick.primitive.selectionType == 'asset') {
                const asset = cesiumAssetManager.get(pick.primitive.getId());

                if (panesService.getPanesState()[k_SHOW_MAP_BOUNDARIES_BANNER]) {
                    // Prevent clicking on non-boundaries when editing boundaries
                    if (asset.assetType.name != 'Boundary') {
                        return selections;
                    }
                } else {
                    // Prevent clicking on boundaries polygons (outlines is ok)
                    if (asset.assetType.name == 'Boundary' && pick.primitive.isPolygon) {
                        return selections;
                    }
                }
            }
            selection = new Selection(pick.primitive.getId(), pick.primitive.selectionType);
        }
        // KML Feature Layers
        else if (pick.id && pick.id._kml && pick.id && pick.id._id) {
            selection = new Selection(pick.id._id, SelectionType.KML_FEATURE_LAYER);
        }
    }

    // If we have a non-duplicate selection, do the thing.
    if (selection && selection.type && !duplicateSelection(selections, selection)) {
        selections.push(selection);
    }
    return selections;
};

function filterHeatMapSelections(selections: Selection[], selection: Selection) {
    if (selection.type !== SelectionType.HEAT_MAP) {
        // We don't want to include heat map entities because we use a
        // a catch all in getFeaturesFromAllHeatmapsAsSelections()
        // Turn a heatmap into appropriate heat points.
        selections.push(selection);
    }
    return selections;
}

/**
 * Ask the GeoDataService for all features at the clicked point.
 * Change those features into things that look like a selectable cesium entity.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {GeoDataService} geoDataService
 * @returns {any[]}
 */
function getFeaturesFromAllHeatmapsAsSelections(
    lat: number,
    lng: number,
    geoDataService: GeoDataService,
): Selection[] {
    const heatMapPointSelections = [];
    // Null selects from everything
    const heatPointsByGeoMapId = geoDataService.lookupSelectableFeatures(null, lat, lng);
    Object.keys(heatPointsByGeoMapId).forEach((id) => {
        heatPointsByGeoMapId[id].forEach((heatPoint) => {
            const geoMapId = parseInt(id);
            heatMapPointSelections.push(
                new Selection(
                    formatSelectableFeatureId(geoMapId, heatPoint),
                    SelectionType.HEAT_MAP,
                ),
            );
        });
    });
    return heatMapPointSelections;
}

// It's possible to select an entity twice with drillpick (There shouldn't be though, there should only be one pickable thing on the map if we do this right)
// This searches through the list of currently selected entities to prevent passing in two of the same entity.
function duplicateSelection(selections: Selection[], selection) {
    // If our entity does not have an ID getter, it can't be a duplicate.
    if (!selection.id) {
        return false;
    }
    for (let i = 0; i < selections.length; ++i) {
        if (selections[i].type === selection.type && selections[i].id === selection.id) {
            return true;
        }
    }
    return false;
}

export class MapInputHandlerForSelections
    extends AbstractMapInputHandler
    implements IMapInputHandler
{
    _selectionService: SelectionService;
    _keypressService: KeypressService;
    _siteStateService: SiteStateService;
    _mapSitesService: MapSitesService;
    _cesiumZoneService: CesiumZoneService;
    _cesiumAssetManager: CesiumAssetManager;
    _mapService: MapService;
    _geoDataService: GeoDataService;
    _panesService: PanesService;
    _$rootScope;

    /**
     * This MapInputHandler handles selecting entities on click and flying to Sites on click.
     */
    constructor(
        selectionService: SelectionService,
        keypressService: KeypressService,
        siteStateService: SiteStateService,
        mapSitesService: MapSitesService,
        cesiumZoneService: CesiumZoneService,
        cesiumAssetManager: CesiumAssetManager,
        mapService: MapService,
        geoDataService: GeoDataService,
        panesService: PanesService,
        $rootScope,
    ) {
        super();
        this._selectionService = selectionService;
        this._keypressService = keypressService;
        this._siteStateService = siteStateService;
        this._mapSitesService = mapSitesService;
        this._cesiumZoneService = cesiumZoneService;
        this._cesiumAssetManager = cesiumAssetManager;
        this._mapService = mapService;
        this._geoDataService = geoDataService;
        this._panesService = panesService;
        this._$rootScope = $rootScope;
    }

    static _pick(
        currentSelections,
        newSelections: Selection[],
        toggleSameSelection,
        multi,
    ): Selection[] {
        if (newSelections.length < 1) {
            return [];
        }

        const mappedSelections = newSelections.map((item) => item?.id);
        const [currentItem = -1] = currentSelections;
        const currentIndex = mappedSelections.indexOf(currentItem.id);
        if (currentIndex === newSelections.length - 1 || currentIndex === -1) {
            return [newSelections[0]];
        }
        return [newSelections[currentIndex + 1]];
    }

    async _click(event, left) {
        const ctrl = this._keypressService.isModifierPressed(CTRL);
        const pickList = this._mapService.viewer.scene.drillPick(event.position);

        const { lat, lng } = this._mapService.mousePositionToLocation(
            event.position.x,
            event.position.y,
        );

        const pickableSelections = pickList
            .reduce(
                (selections, pick) =>
                    pickedToEntitiesReducer(
                        this._cesiumAssetManager,
                        this._panesService,
                        selections,
                        pick,
                    ),
                [],
            )
            .reduce(filterHeatMapSelections, []);

        // Add on our special heat map entities
        // Doing it this way because it seems adding transparent primitives also makes it so that
        // they aren't picked up by cesium drill pick. We collect all heat map selectable features
        // via the geo data service for a given lat, lng then convert it to an entity like object.
        pickableSelections.push(
            ...getFeaturesFromAllHeatmapsAsSelections(lat, lng, this._geoDataService),
        );
        // Finally, convert the entities to selection objects
        const currentSelection = this._selectionService.getSelection();
        const newSelections = MapInputHandlerForSelections._pick(
            currentSelection,
            pickableSelections,
            true,
            ctrl,
        );

        return this._selectionService.setSelection(...newSelections);
    }

    async leftClick(event) {
        await this._click(event, true);
        this._$rootScope.$emit(HIDE_CONTEXT_MENU_EVENT);
    }

    async rightClick(event) {
        await this._click(event, false);
        this._$rootScope.$emit(SHOW_CONTEXT_MENU_EVENT, {
            position: event.position,
            map: true,
        });
    }

    public cleanUp(): void {
        // noop - don't clean anything up as this is the default map handler
        return null;
    }
}
